/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Spinner,
  NotFound,
  NestedWorkout,
  WorkoutHeader,
  WorkoutExercisesList,
  WorkoutExercisesFooter,
  WorkoutExercisesSubHeader,
} from 'web-components';

import {
  Flex,
  WorkoutExercisesView,
} from 'web-styled';

interface Props {
  id: number
  readOnly: boolean
  parentId: number
  parentType: 'workoutTemplates' | 'personalWorkouts',
  resourceType: 'warmups' | 'cooldowns'
}

const AdditionalWorkout = ({
  id,
  readOnly,
  parentId,
  parentType,
  resourceType,
}: Props) => {
  const mapState = useCallback(state => ({
    status: getStatus(
      state,
      [
        `${resourceType}.requests.getFullAdditionalWorkout.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      true,
    ),
    options: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
  }), [id]);

  const { status, options } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestFullWorkout({
          id,
          resourceType: parentType,
          addtionalType: resourceType,
        }),
      );
    }
  }, [id]);

  const type = resourceType === 'warmups' ? 'warmup' : 'cooldown';

  const analyticTrack = (attrs: any) => {
    dispatch({
      type: 'GC_ANALYTICS',
      eventKey: type === 'warmup' ? 'added_warmup' : 'added_cooldown',
      attrs: {
        id: attrs.id,
        name: attrs.name,
        warmupId: attrs.warmup_id,
        cooldownId: attrs.cooldown_id,
      },
    });
  };

  const handleAdd = (payload: any, callbacks = {}) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: parentId,
        payload,
        resourceType: parentType,
        callbacks,
      }),
    );
  };

  return id ? (
    <Flex fillSpace>
      {status.succeeded && (
        <WorkoutExercisesView>
          <WorkoutHeader
            readOnly={readOnly}
            parentId={parentId}
            workoutId={id}
            parentType={parentType}
            resourceType={resourceType}
          />

          {!readOnly && (
            <WorkoutExercisesSubHeader
              workoutId={id}
            />
          )}

          <WorkoutExercisesList
            type={type}
            readOnly={readOnly}
            workoutId={id}
            workoutType={parentType === 'personalWorkouts'
              ? 'PersonalWorkout'
              : 'WorkoutTemplate'}
          />

          {!readOnly && (
            <WorkoutExercisesFooter
              workoutId={id}
              workoutType={parentType === 'personalWorkouts'
                ? 'PersonalWorkout'
                : 'WorkoutTemplate'}
            />
          )}
        </WorkoutExercisesView>
      )}
      {status.pending && (
        <Spinner message="Workout..." />
      )}
      {status.failed && (
        <NotFound
          title="Not Found"
        />
      )}
    </Flex>
  ) : (
    <NestedWorkout
      type={type}
      options={options}
      readOnly={readOnly}
      onRequestAdd={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: value }
          : { cooldown_id: value };

        handleAdd(payload, { onSuccess: analyticTrack });
      }}
      onRequestAddNew={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: '0' }
          : { cooldown_id: '0' };

        const callbacks = {
          onSuccess: (result: any) => {
            dispatch(
              coreActions.requestUpdateEntity({
                id: type === 'warmup' ? result.warmup_id : result.cooldown_id,
                payload: { name: value },
                resourceType,
              }),
            );
            analyticTrack(result);
          },
        };

        handleAdd(payload, callbacks);
      }}
    />
  );
};

export default AdditionalWorkout;
